
/**
 * Complete Invite Page
 * 
 * This page is part of the user invitation flow:
 * 1. Admin sends an invite to a user
 * 2. User clicks on the invite link in their email
 * 3. User is redirected to this page
 * 4. User sets their password
 * 5. User is taken to the TOTP registration page
 * 6. After completing TOTP setup, user are redirected to the appropriate dashboard
 * 
 * Simplified implementation following admin-ui pattern
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { supabase } from '../../lib/supabase'
import { TOTPRegistration } from '../../components/auth/totp-registration'
import Background from '../../components/background'
import StepsIndicator from '../../components/steps-indicator'
import { processAuthLink, cleanupAuthLinkUrl, getRedirectUrlBasedOnRole } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api'
import { env } from '../../lib/env'

export const steps: string[] = ['Setup Password', 'Setup 2FA', 'Finish']

export function CompleteInvitePage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTP, setShowTOTP] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [redirectTo, setRedirectTo] = useState('/admin/dashboard')
  const navigate = useNavigate()
  const { toast } = useToast()

  // Handle auth link processing when component mounts
  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[CompleteInvitePage] useEffect - handleAuthFlow called')
      setIsLoading(true)

      try {
        // First check if we already have a valid session
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.access_token) {
          console.log('[CompleteInvitePage] Already have valid session, no need to process auth link')
          setHasAccessToken(true)
          cleanupAuthLinkUrl(window.location, false)
          setIsLoading(false)
          return
        }

        // If no existing session, try to process auth link
        const result = await processAuthLink(supabase, window.location, 'invite')
        
        if (result.success && result.session) {
          console.log('[CompleteInvitePage] Successfully authenticated via processAuthLink. Session:', result.session)
          setHasAccessToken(true)
          cleanupAuthLinkUrl(window.location, false)
        } else {
          console.log('[CompleteInvitePage] Authentication failed via processAuthLink. Error:', result.error)
          toast({
            title: 'Invalid Invite Link',
            description: 'Your invite link is invalid or has expired. Please contact an administrator for a new invitation.',
            variant: 'destructive',
          })
          navigate('/login', { replace: true })
        }
      } catch (error) {
        console.error('[CompleteInvitePage] Unexpected error in handleAuthFlow:', error)
        toast({
          title: 'Error',
          description: 'Failed to process invite link. Please try again.',
          variant: 'destructive',
        })
        navigate('/login', { replace: true })
      } finally {
        setIsLoading(false)
      }
    }

    handleAuthFlow()
  }, [navigate, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    // Validate password length
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)
    try {
      // Get the current session to verify it's still valid
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Your session has expired. Please use a new invite link.')
      }

      // Update the password
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        throw error
      }

      toast({
        title: 'Password Set',
        description: 'Password has been set successfully. Please complete the security setup by configuring two-factor authentication.',
      })

      // Get user profile to check role and determine redirect
      try {
        const userProfile = await getUserProfile()
        const newRedirectTo = getRedirectUrlBasedOnRole(userProfile, env)
        setRedirectTo(newRedirectTo)
      } catch (profileError) {
        console.error('[CompleteInvitePage] Error getting user profile:', profileError)
        // Continue with default redirect if profile fetch fails
      }

      // All users must complete TOTP enrollment
      setShowTOTP(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to set password'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Background>
      <div className="flex min-h-screen items-center justify-center">
        {!showTOTP ? (
          <>
            {!hasAccessToken ? (
              <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8">
                <Alert>
                  <AlertDescription>Processing invite link...</AlertDescription>
                </Alert>
              </Card>
            ) : (
              <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8">
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col w-full gap-8">
                    <div className="flex flex-col items-center mx-8 gap-2">
                      <h1 className="font-semibold text-lavender-900 text-[40px] leading-[52px]">Setup Password</h1>
                      <p className="font-[400] text-[18px] leading-[32px] text-[#666666]">
                        Create password to activate your account
                      </p>
                    </div>
                    <StepsIndicator currentStep={1} />
                    <div className="flex flex-col gap-4 mt-8">
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-lavender-900">Password *</p>
                        <Input
                          className="rounded-3xl"
                          placeholder="Enter Your Password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isLoading}
                        />
                        <p className="text-gray-600 text-xs">Must contain atleast 8 characters</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-lavender-900">Confirm Password *</p>
                        <Input
                          className="rounded-3xl"
                          placeholder="Re-enter Your Password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={isLoading}
                        />
                        <p className="text-gray-600 text-xs">Both passwords must match</p>
                      </div>
                    </div>
                    {formError && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertDescription>{formError}</AlertDescription>
                      </Alert>
                    )}
                    <Button type="submit" disabled={isLoading} className="rounded-3xl bg-[#354297] hover:bg-[#354297]">
                      {isLoading ? 'Setting Password...' : 'Set Password'}
                    </Button>
                  </div>
                </form>
              </Card>
            )}
          </>
        ) : (
          <TOTPRegistration redirectTo={redirectTo} />
        )}
      </div>
    </Background>
  )
}
